import React from 'react';
import { Card, Text, Flex, Badge } from '@tremor/react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  ChartBarSquareIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import MetricInfo from './MetricInfo';

const COLORS = {
  arr: '#4ade80',       // Soft Green
  dealCount: '#1e40af', // Deep Blue
  winRate: '#00CBC0',   // Teal
  avgAcv: '#f59e0b',   // Amber
};

const formatCurrency = (value) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value}`;
};

const formatMonth = (monthStr) => {
  if (!monthStr) return '';
  const [year, month] = monthStr.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0]?.payload || {};

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200 p-3 min-w-[200px]">
      <Text className="font-semibold text-gray-900 mb-2">{label}</Text>
      <div className="space-y-1.5">
        <Flex justifyContent="between" className="text-sm">
          <Flex alignItems="center" className="gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.arr }} />
            <Text className="text-gray-600">Total ARR</Text>
          </Flex>
          <Text className="font-mono font-medium text-gray-900">
            {formatCurrency(data.total_arr || 0)}
          </Text>
        </Flex>
        <Flex justifyContent="between" className="text-sm">
          <Flex alignItems="center" className="gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.dealCount }} />
            <Text className="text-gray-600">Deals Won</Text>
          </Flex>
          <Text className="font-mono font-medium text-gray-900">
            {data.deal_count || 0}
          </Text>
        </Flex>
        <Flex justifyContent="between" className="text-sm">
          <Flex alignItems="center" className="gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.avgAcv }} />
            <Text className="text-gray-600">Avg ACV</Text>
          </Flex>
          <Text className="font-mono font-medium text-gray-900">
            {data.deal_count > 0 ? formatCurrency(Math.round((data.total_arr || 0) / data.deal_count)) : 'N/A'}
          </Text>
        </Flex>
        <Flex justifyContent="between" className="text-sm">
          <Flex alignItems="center" className="gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.winRate }} />
            <Text className="text-gray-600">Win Rate</Text>
          </Flex>
          <Text className="font-mono font-medium text-gray-900">
            {data.win_rate_pct || 0}%
          </Text>
        </Flex>
      </div>
    </div>
  );
};

const HistoricalPerformance = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Card className="bg-white/80 backdrop-blur-2xl rounded-3xl border border-white/20 shadow-xl">
        <div className="flex items-center justify-center h-64 text-gray-400">
          <ChartBarSquareIcon className="h-8 w-8 mr-2" />
          <span>No closed deals data available</span>
        </div>
      </Card>
    );
  }

  // Sort chronologically for chart display, compute avg ACV
  const chartData = [...data].reverse().map(item => ({
    ...item,
    monthLabel: formatMonth(item.month),
    avg_acv: item.deal_count > 0 ? Math.round((item.total_arr || 0) / item.deal_count) : 0,
  }));

  // Current vs previous month for velocity card
  const currentMonth = data[0] || {};
  const previousMonth = data[1] || {};
  const velocityDelta = currentMonth.avg_days_to_close - (previousMonth.avg_days_to_close || 0);
  const velocityImproved = velocityDelta < 0;

  // Overall win rate from latest month
  const currentWinRate = currentMonth.win_rate_pct || 0;

  // Totals
  const totalARR = data.reduce((sum, m) => sum + (m.total_arr || 0), 0);
  const totalDeals = data.reduce((sum, m) => sum + (m.deal_count || 0), 0);
  const overallAvgAcv = totalDeals > 0 ? Math.round(totalARR / totalDeals) : 0;
  const currentAvgAcv = currentMonth.deal_count > 0 ? Math.round((currentMonth.total_arr || 0) / currentMonth.deal_count) : 0;

  return (
    <Card className="bg-white/80 backdrop-blur-2xl rounded-3xl border border-white/20 shadow-xl">
      {/* Header */}
      <Flex justifyContent="between" alignItems="start" className="mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-white/60 rounded-xl">
            <ChartBarSquareIcon className="h-5 w-5 text-[#809292]" />
          </div>
          <div>
            <Text className="font-semibold text-gray-900">Sales Performance</Text>
            <Text className="text-xs text-gray-500">
              Monthly closed-won ARR & deal count (last 12 months)
            </Text>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge color="emerald" size="sm">
            Win Rate: {currentWinRate}%
          </Badge>
          <MetricInfo
            title="Win Rate"
            description="Percentage of all closed deals (won + lost) that were won. Higher is better."
          />
        </div>
      </Flex>

      {/* Summary Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <div className="bg-white/60 rounded-xl p-3">
          <Text className="text-xs text-gray-500 mb-1">Total ARR (Period)</Text>
          <Text className="text-lg font-bold text-gray-900 font-mono">
            {formatCurrency(totalARR)}
          </Text>
        </div>
        <div className="bg-white/60 rounded-xl p-3">
          <Text className="text-xs text-gray-500 mb-1">Deals Won</Text>
          <Text className="text-lg font-bold text-gray-900 font-mono">
            {totalDeals}
          </Text>
        </div>
        <div className="bg-amber-50/60 rounded-xl p-3 ring-1 ring-amber-100">
          <Text className="text-xs text-amber-700 mb-1 font-semibold">Avg ACV</Text>
          <Text className="text-lg font-bold text-amber-600 font-mono">
            {formatCurrency(overallAvgAcv)}
          </Text>
          <Text className="text-[10px] text-gray-400 mt-0.5">
            This month: {currentAvgAcv > 0 ? formatCurrency(currentAvgAcv) : 'N/A'}
          </Text>
        </div>
        <div className="bg-white/60 rounded-xl p-3">
          <Flex alignItems="center" className="gap-1 mb-1">
            <ClockIcon className="h-3 w-3 text-gray-400" />
            <Text className="text-xs text-gray-500">Avg Days to Close</Text>
          </Flex>
          <Flex alignItems="baseline" className="gap-2">
            <Text className="text-lg font-bold text-gray-900 font-mono">
              {currentMonth.avg_days_to_close || 0}d
            </Text>
            {previousMonth.avg_days_to_close > 0 && (
              <Flex alignItems="center" className="gap-0.5">
                {velocityImproved ? (
                  <ArrowTrendingDownIcon className="h-3 w-3 text-emerald-500" />
                ) : (
                  <ArrowTrendingUpIcon className="h-3 w-3 text-pink-500" />
                )}
                <Text className={`text-xs font-mono ${velocityImproved ? 'text-emerald-600' : 'text-pink-600'}`}>
                  {Math.abs(velocityDelta)}d vs prev
                </Text>
              </Flex>
            )}
          </Flex>
        </div>
        <div className="bg-white/60 rounded-xl p-3">
          <Text className="text-xs text-gray-500 mb-1">Current Win Rate</Text>
          <Text className="text-lg font-bold text-gray-900 font-mono">
            {currentWinRate}%
          </Text>
        </div>
      </div>

      {/* Combo Chart */}
      <div className="h-64 sm:h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
            <XAxis
              dataKey="monthLabel"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: '#6B7280' }}
              dy={10}
            />
            <YAxis
              yAxisId="left"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: '#6B7280' }}
              tickFormatter={(v) => {
                if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
                if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
                return `$${v}`;
              }}
              width={60}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: '#6B7280' }}
              allowDecimals={false}
              width={40}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="bottom"
              height={36}
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: '12px' }}
            />
            <Bar
              yAxisId="left"
              dataKey="total_arr"
              name="Total ARR"
              fill={COLORS.arr}
              radius={[4, 4, 0, 0]}
              barSize={32}
              opacity={0.85}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="avg_acv"
              name="Avg ACV"
              stroke={COLORS.avgAcv}
              strokeWidth={2.5}
              strokeDasharray="6 3"
              dot={{ r: 4, fill: COLORS.avgAcv, strokeWidth: 2, stroke: '#fff' }}
              activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="deal_count"
              name="Deals Won"
              stroke={COLORS.dealCount}
              strokeWidth={2.5}
              dot={{ r: 4, fill: COLORS.dealCount, strokeWidth: 2, stroke: '#fff' }}
              activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

export default HistoricalPerformance;
